import { Card, CardContent, CardHeader, CardTitle } from '../Card';
import { Badge } from '../Badge';
import { Upload, Play, BarChart3, Download, FileText, Zap } from 'lucide-react';

export default function QuickstartGuide() {
  return (
    <div className="mx-auto mb-8 p-4">
      <div className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold text-white">Quickstart Guide</h1>
        <p className="text-lg text-gray-300">Get started with Mithrl in minutes</p>
      </div>

      <div className="grid gap-6">
        <Card className="border-gray-700 bg-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Zap className="h-8 w-8 text-yellow-400" />
              Welcome to Mithrl
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-8 text-gray-300">
            <p className="text-sm">
              Mithrl's mission is to empower scientists to accelerate breakthroughs by integrating AI-driven agents that
              automate complex data analysis. The platform enables researchers across disciplines to derive actionable
              biological insights from omics data with unprecedented speed and precision, turning months of manual work
              into minutes.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-blue-900 text-[1.2rem] text-blue-200">High-throughput processing</Badge>
              <Badge className="bg-green-900 text-[1.2rem] text-green-200">Cloud-based analysis</Badge>
              <Badge className="bg-purple-900 text-[1.2rem] text-purple-200">Real-time results</Badge>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 gap-6 xs:grid-cols-1">
          <Card className="border-gray-700 bg-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">
                  1
                </div>
                <Upload className="mx-2 h-8 w-8 text-blue-400" />
                Upload Data
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8 text-gray-300">
              <p className="text-[1.5rem]">
                Start by importing your FASTQ or SRA files to the Mithrl platform. We support both single-end and
                paired-end sequencing data.
              </p>
              <ul className="list-inside list-disc space-y-1 text-sm">
                <li>Drag and drop files or use the Mithrl Cloud browser</li>
                <li>Supports .fastq, .fq, and compressed formats (.gz)</li>
                <li>No file size limit</li>
                <li>Batch upload multiple samples</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-gray-700 bg-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-600 text-sm font-bold text-white">
                  2
                </div>
                <FileText className="mx-2 h-8 w-8 text-green-400" />
                Configure Parameters
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8 text-gray-300">
              <p className="text-[1.5rem]">Set up your metadata and reference genome selection for optimal results.</p>
              <ul className="list-inside list-disc space-y-1 text-sm">
                <li>Choose reference genome (Human, Mouse, etc.)</li>
                <li>Single Cell RNA-Seq, Bulk RNA-Seq, or Single Cell Methylation modalities</li>
                <li>Support for uploading external metadata</li>
                <li>Metadata cleaning for accuracy and completeness</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-gray-700 bg-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-600 text-sm font-bold text-white">
                  3
                </div>
                <Play className="mx-2 h-8 w-8 text-purple-400" />
                Run Alignment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8 text-gray-300">
              <p className="text-[1.5rem]">
                Execute the alignment process and monitor progress in real-time through our dashboard.
              </p>
              <ul className="list-inside list-disc space-y-1 text-sm">
                <li>Real-time progress tracking</li>
                <li>Run multiple samples in parallel</li>
                <li>Email notifications when complete</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-gray-700 bg-gray-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-600 text-sm font-bold text-white">
                  4
                </div>
                <BarChart3 className="mx-2 h-8 w-8 text-orange-400" />
                Analyze Results
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8 text-gray-300">
              <p className="text-[1.5rem]">Review alignment statistics and ask questions in natural language</p>
              <ul className="list-inside list-disc space-y-1 text-sm">
                <li>Use suggested questions to speed up your analysis</li>
                <li>Perform complex analyses with our AI co-scientist</li>
                <li>Visualizations and reports</li>
                <li>Key findings and summaries</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <Card className="border-gray-700 bg-gray-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Download className="h-8 w-8 text-cyan-400" />
              Download & Export
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-gray-300">
            <p className="text-[1.5rem]">Export your results for downstream analysis and publication.</p>
            <div className="mt-4 grid grid-cols-3 gap-4 xs:grid-cols-1">
              <div className="rounded-lg bg-gray-700 p-4">
                <h4 className="mb-1 text-[1.5rem] font-medium text-white">Analysis Code</h4>
                <p className="text-sm text-gray-400">Generate code for your analysis</p>
              </div>
              <div className="rounded-lg bg-gray-700 p-4">
                <h4 className="mb-1 text-[1.5rem] font-medium text-white">QC Report</h4>
                <p className="text-sm text-gray-400">View QC statistics, sequence counts, and more</p>
              </div>
              <div className="rounded-lg bg-gray-700 p-4">
                <h4 className="mb-1 text-[1.5rem] font-medium text-white">Visualizations and Tables</h4>
                <p className="text-sm text-gray-400">Download PNG and CSV files</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-700 bg-gradient-to-r from-blue-900 to-purple-900">
          <CardContent className="p-6">
            <div className="text-center">
              <h3 className="mb-2 text-lg font-semibold text-white">Need Help?</h3>
              <p className="mb-4 text-[1.2rem] text-blue-200">
                Our support team is here to help you get the most out of Mithrl
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Badge className="bg-white text-[1.2rem] text-blue-900 hover:bg-gray-100">Documentation</Badge>
                <Badge className="bg-white text-[1.2rem] text-blue-900 hover:bg-gray-100">Video Tutorials</Badge>
                <Badge className="bg-white text-[1.2rem] text-blue-900 hover:bg-gray-100">Live Chat Support</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
